"use client";

import { useState, useEffect, useCallback } from "react";
import Modal from "@/app/components/common/Modal";
import {
  fetchFriends,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  removeFriend,
} from "@/app/lib/api";
import type {
  FriendResponse,
  FriendRequestResponse,
} from "@/app/lib/types";

interface FriendManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FriendManager({ isOpen, onClose }: FriendManagerProps) {
  const [friends, setFriends] = useState<FriendResponse[]>([]);
  const [pendingSent, setPendingSent] = useState<FriendRequestResponse[]>([]);
  const [pendingReceived, setPendingReceived] = useState<FriendRequestResponse[]>([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadFriends = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchFriends();
      setFriends(data.friends);
      setPendingSent(data.pending_sent);
      setPendingReceived(data.pending_received);
    } catch (err) {
      setError("Failed to load friends");
      console.error("Failed to load friends:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadFriends();
      setError(null);
      setSuccess(null);
      setEmail("");
    }
  }, [isOpen, loadFriends]);

  const handleSendRequest = async () => {
    if (!email.trim()) return;
    setError(null);
    setSuccess(null);
    try {
      const resp = await sendFriendRequest(email.trim());
      const who = resp?.to_name || resp?.to_email || email.trim();
      setSuccess(
        resp?.status === "accepted"
          ? `You are now friends with ${who}`
          : `Request sent to ${who}`
      );
      setEmail("");
      loadFriends();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send request";
      // Server returns JSON like {"detail":"..."} — extract detail if present
      try {
        const parsed = JSON.parse(msg);
        setError(parsed.detail || msg);
      } catch {
        setError(msg);
      }
    }
  };

  const handleAccept = async (id: string) => {
    setError(null);
    try {
      await acceptFriendRequest(id);
      loadFriends();
    } catch (err) {
      setError("Failed to accept request");
      console.error("Failed to accept friend request:", err);
    }
  };

  const handleDecline = async (id: string) => {
    setError(null);
    try {
      await declineFriendRequest(id);
      loadFriends();
    } catch (err) {
      setError("Failed to decline request");
      console.error("Failed to decline friend request:", err);
    }
  };

  const handleRemove = async (id: string) => {
    setError(null);
    try {
      await removeFriend(id);
      loadFriends();
    } catch (err) {
      setError("Failed to remove friend");
      console.error("Failed to remove friend:", err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Friends">
      <div style={{ padding: "0 16px 16px", minWidth: "320px", maxHeight: "70vh", overflowY: "auto" }}>
        {/* Status messages */}
        {error && (
          <div style={{
            padding: "8px 12px",
            marginBottom: "12px",
            borderRadius: "var(--radius)",
            background: "rgba(239, 68, 68, 0.15)",
            color: "#ef4444",
            fontSize: "13px",
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            padding: "8px 12px",
            marginBottom: "12px",
            borderRadius: "var(--radius)",
            background: "rgba(34, 197, 94, 0.15)",
            color: "#22c55e",
            fontSize: "13px",
          }}>
            {success}
          </div>
        )}

        {/* Add friend */}
        <div style={{ marginBottom: "20px" }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Add Friend
          </h3>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSendRequest(); }}
              placeholder="Enter name or email"
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text-primary)",
                fontSize: "14px",
                outline: "none",
              }}
            />
            <button
              className="btn-primary"
              onClick={handleSendRequest}
              disabled={!email.trim()}
              style={{ padding: "8px 16px", fontSize: "13px", whiteSpace: "nowrap" }}
            >
              Send Request
            </button>
          </div>
        </div>

        {/* Pending received */}
        {pendingReceived.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Pending Requests
            </h3>
            {pendingReceived.map((req) => (
              <div
                key={req.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span style={{ fontSize: "14px", color: "var(--text-primary)" }}>
                  {req.from_email}
                </span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    className="btn-primary"
                    onClick={() => handleAccept(req.id)}
                    style={{ padding: "4px 12px", fontSize: "12px" }}
                  >
                    Accept
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => handleDecline(req.id)}
                    style={{ padding: "4px 12px", fontSize: "12px", color: "var(--text-secondary)" }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Friends list */}
        <div style={{ marginBottom: "20px" }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Friends{friends.length > 0 ? ` (${friends.length})` : ""}
          </h3>
          {loading && friends.length === 0 ? (
            <div style={{ padding: "16px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
              Loading...
            </div>
          ) : friends.length === 0 ? (
            <div style={{ padding: "16px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
              No friends yet. Send a request above.
            </div>
          ) : (
            friends.map((friend) => (
              <div
                key={friend.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div>
                  <div style={{ fontSize: "14px", color: "var(--text-primary)" }}>
                    {friend.display_name || friend.email}
                  </div>
                  {friend.display_name && (
                    <div style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
                      {friend.email}
                    </div>
                  )}
                </div>
                <button
                  className="btn-ghost"
                  onClick={() => handleRemove(friend.id)}
                  style={{ padding: "4px 10px", fontSize: "12px", color: "#ef4444" }}
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>

        {/* Pending sent */}
        {pendingSent.length > 0 && (
          <div>
            <h3 style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Sent Requests
            </h3>
            {pendingSent.map((req) => (
              <div
                key={req.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span style={{ fontSize: "14px", color: "var(--text-primary)" }}>
                  {req.to_email}
                </span>
                <span style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
                  {req.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
